(() => {
  if (customElements.get('borderless-strip')) return;

  class BorderlessStrip extends HTMLElement {
    connectedCallback() {
      this.setRandomPositions();
      this.setupHoverPause();
      this.setupResizeObserver();
      this.schedulePostLayout();
      this.bindMediaLoad();
    }

    disconnectedCallback() {
      if (this._postLayoutRaf) {
        window.cancelAnimationFrame(this._postLayoutRaf);
        this._postLayoutRaf = null;
      }
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      if (this._resizeTimer) {
        window.clearTimeout(this._resizeTimer);
        this._resizeTimer = null;
      }
      if (this._hoverHandlers) {
        this._hoverHandlers.forEach(({ label, enter, leave }) => {
          label.removeEventListener('mouseenter', enter);
          label.removeEventListener('mouseleave', leave);
        });
        this._hoverHandlers = null;
      }
      if (this.dataset.pauseBound) {
        delete this.dataset.pauseBound;
      }
      if (this._mediaLoadHandlers) {
        this._mediaLoadHandlers.forEach(({ img, onLoad }) => {
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onLoad);
        });
        this._mediaLoadHandlers = null;
      }
    }

    setupResizeObserver() {
      if (this._resizeObserver) return;
      this._resizeObserver = new ResizeObserver(() => {
        window.clearTimeout(this._resizeTimer);
        this._resizeTimer = window.setTimeout(() => this.setRandomPositions(), 120);
      });
      this._resizeObserver.observe(this);
    }

    schedulePostLayout() {
      if (this._postLayoutRaf) return;
      this._postLayoutRaf = window.requestAnimationFrame(() => {
        this._postLayoutRaf = null;
        this.setRandomPositions();
      });
    }

    bindMediaLoad() {
      const images = Array.from(this.querySelectorAll('img'));
      if (images.length === 0) return;
      this._mediaLoadHandlers = [];

      images.forEach((img) => {
        if (img.complete) return;
        const onLoad = () => {
          this.setRandomPositions();
        };
        img.addEventListener('load', onLoad, { once: true });
        img.addEventListener('error', onLoad, { once: true });
        this._mediaLoadHandlers.push({ img, onLoad });
      });
    }

    setRandomPositions() {
      const min = Number(this.dataset.danmakuMin || 10);
      const max = Number(this.dataset.danmakuMax || 90);
      const bullets = Array.from(this.querySelectorAll('[data-borderless-bullet]'));
      const layer = this.querySelector('[data-borderless-layer]');
      if (!layer || bullets.length === 0) return;

      const layerRect = layer.getBoundingClientRect();
      const used = [];
      const gap = 12;

      bullets.forEach((bullet) => {
        const bulletRect = bullet.getBoundingClientRect();
        const width = Math.max(bulletRect.width, 1);
        const height = Math.max(bulletRect.height, 1);
        const bounds = BorderlessStrip.getBounds(layerRect, width, height, min, max);

        const position = BorderlessStrip.pickNonOverlappingPosition(bounds, width, height, used, gap, 24);
        used.push(position);
        bullet.style.left = `${position.left}px`;
        bullet.style.top = `${position.top}px`;
      });
    }

    setupHoverPause() {
      const labels = this.querySelectorAll('[data-borderless-label]');
      if (labels.length === 0) return;
      if (this.dataset.pauseBound === 'true') return;
      if (!this.classList.contains('borderless-strip--pause')) return;
      this.dataset.pauseBound = 'true';
      this._hoverHandlers = [];

      labels.forEach((label) => {
        const enter = () => {
          const bullet = label.closest('[data-borderless-bullet]');
          if (!bullet) return;
          bullet.classList.add('borderless-strip__bullet--paused');
        };
        const leave = () => {
          const bullet = label.closest('[data-borderless-bullet]');
          if (!bullet) return;
          bullet.classList.remove('borderless-strip__bullet--paused');
        };
        label.addEventListener('mouseenter', enter);
        label.addEventListener('mouseleave', leave);
        this._hoverHandlers.push({ label, enter, leave });
      });
    }

    static getBounds(layerRect, width, height, minPercent, maxPercent) {
      const safeMin = Number.isFinite(minPercent) ? minPercent : 10;
      const safeMax = Number.isFinite(maxPercent) ? maxPercent : 90;
      const minX = (layerRect.width * safeMin) / 100;
      const maxX = Math.max(minX, (layerRect.width * safeMax) / 100 - width);
      const minY = (layerRect.height * safeMin) / 100;
      const maxY = Math.max(minY, (layerRect.height * safeMax) / 100 - height);
      return { minX, maxX, minY, maxY };
    }

    static pickNonOverlappingPosition(bounds, width, height, used, gap, attempts) {
      for (let i = 0; i < attempts; i += 1) {
        const left = BorderlessStrip.randomBetween(bounds.minX, bounds.maxX);
        const top = BorderlessStrip.randomBetween(bounds.minY, bounds.maxY);
        const rect = { left, top, right: left + width, bottom: top + height };
        const hasOverlap = used.some((placed) => BorderlessStrip.overlaps(rect, placed, gap));
        if (!hasOverlap) return rect;
      }
      const fallbackLeft = BorderlessStrip.randomBetween(bounds.minX, bounds.maxX);
      const fallbackTop = BorderlessStrip.randomBetween(bounds.minY, bounds.maxY);
      return {
        left: fallbackLeft,
        top: fallbackTop,
        right: fallbackLeft + width,
        bottom: fallbackTop + height
      };
    }

    static overlaps(a, b, gap) {
      return !(
        a.right + gap < b.left ||
        a.left > b.right + gap ||
        a.bottom + gap < b.top ||
        a.top > b.bottom + gap
      );
    }

    static randomBetween(min, max) {
      const safeMin = Number.isFinite(min) ? min : 10;
      const safeMax = Number.isFinite(max) ? max : 90;
      if (safeMax <= safeMin) return safeMin;
      return safeMin + Math.random() * (safeMax - safeMin);
    }
  }

  customElements.define('borderless-strip', BorderlessStrip);
})();
